import { Link } from "react-router-dom";

const CONTENT = {
  "how-it-works": {
    title: "How It Works",
    intro: "The AI Scale Force journey is built around one seamless flow: Discover → Webinar → Offer → Purchase → Learn → Certificate.",
    sections: [
      ["1. Choose a webinar", "Pick your industry and we surface the single next best live session in your local time — no giant calendars."],
      ["2. Reserve your seat", "Register in seconds. We handle timezone and currency automatically."],
      ["3. Attend the 45-minute session", "Join a focused, practical live masterclass led by an industry expert."],
      ["4. Unlock the special offer", "Attendees become eligible for exclusive webinar-only course bundles."],
      ["5. Learn & get certified", "Access courses instantly, track your progress, and earn a verifiable certificate."],
    ],
  },
  about: {
    title: "About AI Scale Force Store",
    intro: "AI Scale Force Store is a premium online learning and webinar commerce platform offering practical, industry-focused courses and live webinars.",
    sections: [
      ["Our mission", "Help professionals learn AI and master high-growth industries — Real Estate, Insurance and Loans — through practical, no-fluff education."],
      ["What makes us different", "A smart concierge experience: we detect your location, currency and timezone, then surface exactly the right next webinar for you."],
      ["Certificates that count", "Every completed course earns a verifiable AI Scale Force certificate."],
    ],
  },
  contact: {
    title: "Contact Us",
    intro: "We'd love to hear from you. Reach our team by phone, email, or visit us in Chennai.",
    sections: [
      ["Call us — India", "89259 37578 (Ph)"],
      ["Call us — Global", "+1 478 400 7485"],
      ["Email", "support@aiscaleforce.store"],
      ["Visit us", "RMZ Millenia Phase 2, Campus 2, 10th Floor, MGR Salai, Perungudi, Chennai 600096"],
      ["Support hours", "Monday–Saturday, 9:30 AM – 9:30 PM IST"],
      ["Our network", "aiscaleforce.com · aigigjobs.com · aiscaleforce.in · gtforce.world"],
    ],
  },
  terms: {
    title: "Terms & Conditions",
    intro: "These Terms & Conditions govern your use of AI Scale Force Store (aiscaleforce.store) and any purchases made on the platform. By accessing the site or buying a course, webinar seat, or bundle, you agree to these terms.",
    sections: [
      ["About us", "AI Scale Force Store is an online learning and webinar platform providing digital courses and live webinars across AI, Real Estate, Insurance and Loans. Registered office: RMZ Millenia Phase 2, Campus 2, 10th Floor, MGR Salai, Perungudi, Chennai 600096. Contact: support@aiscaleforce.store · 89259 37578."],
      ["Products & services", "We sell digital, self-paced courses, live 45-minute webinars, and course bundles. Access is granted to your registered account and is for personal, non-transferable use only."],
      ["Pricing & payments", "Prices are shown in INR (₹) for Indian customers and USD ($) for international customers, inclusive of applicable taxes where stated. Payments in India are processed securely by Razorpay; international payments by Stripe. We never store your full card details. You will always see the exact amount and currency before confirming payment."],
      ["Accounts", "You are responsible for keeping your login credentials secure and for all activity under your account."],
      ["Acceptable use", "You may not share, resell, record, redistribute or publicly display course content or webinar sessions without written permission."],
      ["Intellectual property", "All course material, videos, and branding are the property of AI Scale Force Store and protected by applicable laws."],
      ["Governing law", "These terms are governed by the laws of India, with jurisdiction in Chennai, Tamil Nadu."],
    ],
  },
  privacy: {
    title: "Privacy Policy",
    intro: "This Privacy Policy explains how AI Scale Force Store collects, uses and protects your information when you use aiscaleforce.store.",
    sections: [
      ["Information we collect", "Name, email, phone number, country, city and timezone; webinar registrations; purchase and enrollment history; and basic usage/analytics data used to personalise your experience."],
      ["Payment information", "Card and payment details are collected and processed directly by our payment partners — Razorpay (India) and Stripe (international). We do NOT store your full card number, CVV, or banking credentials on our servers."],
      ["How we use your data", "To deliver courses and webinars, process payments, issue certificates, send transactional emails (confirmations, reminders, receipts) and, with your consent, relevant updates and offers."],
      ["Cookies", "We use cookies/local storage to remember your location, currency, and preferences, and to keep you signed in."],
      ["Third parties", "We share data only with service providers necessary to run the platform (e.g. payment gateways, email delivery). We do not sell your personal data."],
      ["Your rights", "You may request access, correction or deletion of your personal data by emailing support@aiscaleforce.store."],
      ["Contact", "For any privacy questions: support@aiscaleforce.store · 89259 37578 · RMZ Millenia Phase 2, Campus 2, 10th Floor, MGR Salai, Perungudi, Chennai 600096."],
    ],
  },
  refund: {
    title: "Refund & Cancellation Policy",
    intro: "We want you to be satisfied with your purchase. This policy explains refunds and cancellations for courses and bundles bought on AI Scale Force Store.",
    sections: [
      ["Eligibility", "A refund may be requested within 7 days of purchase provided you have completed less than 20% of the course content. Bundles follow the same policy applied across the included courses."],
      ["How to request", "Email support@aiscaleforce.store from your registered email with your order/invoice number (format ASF-INV-XXXXXXXX). Our team will review and respond within 2 business days."],
      ["Processing time", "Approved refunds are credited back to your original payment method via Razorpay (India) or Stripe (international) within 5–7 business days of approval."],
      ["Webinar registrations", "Live webinars are free to register for, so no payment/refund applies to webinar seats."],
      ["Non-refundable cases", "Refunds are not available after 7 days, once more than 20% of a course is completed, or where a certificate has already been issued."],
      ["Contact", "For any billing or refund query: support@aiscaleforce.store · 89259 37578."],
    ],
  },
  cookies: { title: "Cookie Policy", intro: "We use cookies to remember your location, currency and preferences.", sections: [["Essential cookies", "Required for login and checkout."], ["Preference cookies", "Store your country, currency and topic choices."]] },
};

export default function StaticPage({ page }) {
  const c = CONTENT[page] || CONTENT.about;
  return (
    <div className="max-w-3xl mx-auto px-6 py-20" data-testid={`static-${page}`}>
      <div className="asf-overline mb-3">AI Scale Force Store</div>
      <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight">{c.title}</h1>
      <p className="text-lg text-slate-600 mt-4">{c.intro}</p>
      <div className="mt-10 space-y-6">
        {c.sections.map(([t, d]) => (
          <div key={t} className="asf-card p-6">
            <h3 className="font-display text-xl font-semibold">{t}</h3>
            <p className="text-slate-500 mt-2">{d}</p>
          </div>
        ))}
      </div>
      <Link to="/webinars" className="btn-primary inline-block mt-10 px-8 py-4">Join a Webinar</Link>
    </div>
  );
}
